import { useState } from "react";
import Reveal from "@/components/Reveal";
import { MessageCircle, Phone, Mail, Send, CheckCircle2, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import site, { waLink } from "@/config/site";
import { submitLead } from "@/lib/api";

const inputCls =
  "w-full bg-[#0A0F08] border border-[#27331C] rounded-xl px-4 py-3.5 text-sm text-[#F2F1E8] placeholder:text-[#5A5D4E] focus:outline-none focus:border-[var(--accent)] transition-colors duration-300";

export const LeadForm = ({ plan = "growth-call", compact = false }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", brand: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Name, email and phone are required.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({ ...form, plan });
      setDone(true);
      toast.success("Details received! Check your inbox.");
    } catch {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="lead-success" className="text-center py-10">
        <CheckCircle2 className="w-12 h-12 text-[var(--accent)] mx-auto mb-4" />
        <h3 className="font-display text-3xl text-[#F2F1E8]">You're in.</h3>
        <p className="mt-3 text-sm text-[#8A8C7E] max-w-sm mx-auto">
          Your details have landed safely. You'll hear back personally within 24 hours —
          with your payment link and slot options.
        </p>
        {waLink() && (
          <a
            href={waLink("Hi! I just submitted my details for the Growth Call.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="lead-whatsapp-btn"
            className="mt-6 inline-flex items-center gap-2 bg-[var(--accent)] text-[#0A0F08] font-bold text-sm px-7 py-3.5 rounded-full hover:bg-[#F2F1E8] transition-colors duration-300"
          >
            <MessageCircle className="w-4 h-4" /> Continue on WhatsApp
          </a>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="lead-form" className="space-y-4">
      <div className={`grid ${compact ? "" : "sm:grid-cols-2"} gap-4`}>
        <input data-testid="lead-name-input" className={inputCls} placeholder="Your name *" value={form.name} onChange={set("name")} />
        <input data-testid="lead-phone-input" className={inputCls} placeholder="Phone / WhatsApp *" value={form.phone} onChange={set("phone")} />
      </div>
      <input data-testid="lead-email-input" type="email" className={inputCls} placeholder="Email address *" value={form.email} onChange={set("email")} />
      <input data-testid="lead-brand-input" className={inputCls} placeholder="Brand / business name" value={form.brand} onChange={set("brand")} />
      {!compact && (
        <textarea
          data-testid="lead-message-input"
          className={`${inputCls} min-h-[96px] resize-none`}
          placeholder="What's your biggest growth challenge right now?"
          value={form.message}
          onChange={set("message")}
        />
      )}
      <button
        type="submit"
        data-testid="lead-submit-btn"
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 bg-[var(--accent)] text-[#0A0F08] font-bold text-sm sm:text-base px-8 py-4 rounded-full hover:bg-[#F2F1E8] transition-colors duration-300 disabled:opacity-60"
      >
        {loading ? "Sending…" : "Send My Details"}
        <Send className="w-4 h-4" />
      </button>
      <p className="text-center font-mono2 text-[10px] tracking-[0.15em] text-[#5A5D4E]">
        YOUR DETAILS GO DIRECTLY TO THE FOUNDER · NO SPAM, EVER
      </p>
    </form>
  );
};

const Connect = () => (
  <section data-testid="connect-section" className="relative border-t border-[#27331C] bg-[#0E140A]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32 grid lg:grid-cols-2 gap-14 items-start">
      <Reveal>
        <div className="flex items-baseline gap-4 mb-6">
          <span className="font-mono2 text-xs tracking-[0.3em] text-[var(--accent)]">06</span>
          <span className="h-px flex-1 bg-[#27331C]" />
          <span className="font-mono2 text-xs tracking-[0.3em] text-[#8A8C7E]">REACH OUT</span>
        </div>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#F2F1E8]">
          Tell me where<br />
          <span className="italic font-light text-[var(--accent)]">your brand hurts.</span>
        </h2>
        <p className="mt-6 text-sm sm:text-base text-[#B9BBAC] leading-relaxed max-w-md">
          Drop your details and you'll hear back personally within 24 hours.
          In a hurry? Skip the queue — reach out directly.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          {site.schedule.calendly && (
            <a href={site.schedule.calendly} target="_blank" rel="noopener noreferrer" data-testid="quick-schedule-btn"
              className="flex items-center gap-2 bg-[#F2F1E8] text-[#0A0F08] font-bold text-sm px-6 py-3.5 rounded-full hover:bg-[var(--accent)] transition-colors duration-300">
              <CalendarCheck className="w-4 h-4" /> Book Free Consultation
            </a>
          )}
          {site.contact.whatsapp && (
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="quick-whatsapp-btn"
              className="flex items-center gap-2 bg-[var(--accent)] text-[#0A0F08] font-bold text-sm px-6 py-3.5 rounded-full hover:bg-[#F2F1E8] transition-colors duration-300">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          )}
          {site.contact.phone && (
            <a href={`tel:${site.contact.phone}`} data-testid="quick-call-btn"
              className="flex items-center gap-2 border border-[#27331C] text-[#F2F1E8] text-sm px-6 py-3.5 rounded-full hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors duration-300">
              <Phone className="w-4 h-4" /> Call Now
            </a>
          )}
          {site.contact.email && (
            <a href={`mailto:${site.contact.email}`} data-testid="quick-email-btn"
              className="flex items-center gap-2 border border-[#27331C] text-[#F2F1E8] text-sm px-6 py-3.5 rounded-full hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors duration-300">
              <Mail className="w-4 h-4" /> Email
            </a>
          )}
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="rounded-3xl border border-[#27331C] bg-[#0A0F08] p-7 sm:p-9">
          <LeadForm />
        </div>
      </Reveal>
    </div>
  </section>
);

export default Connect;
